import API from "api/client";
import { TargetThemes } from "consts";
import { envConfig } from "config";

/**
 * @typedef { import('types').Target } Target
 * @typedef { import('types').TargetType } TargetType
 * @typedef { import('types').TargetCreate} TargetCreate
 */

const { targetsAPI } = envConfig;

/**
 * @param {TargetCreate} request - The target values being updated.
 * @returns {Promise<Target>}
 */
const createTarget = (request) => API.post(`${targetsAPI}`, request).then((res) => res.data);

/**
 * @param {number} id
 * @param {TargetCreate} request
 * @returns {Promise<Target>}
 */
const cloneTarget = (id, request) => API.post(`${targetsAPI}${id}/clone`, request).then((res) => res.data);

/**
 * @param id {number} The ID of the target to be deleted.
 * @returns {Promise<void>}
 */
const deleteTarget = (id) => API.delete(`${targetsAPI}${id}`).then((res) => res.data);

/**
 * @param id {int|null} The ID of the target to be updated.
 * @returns {Promise<{}>}
 */
const discardTargetDraftTheme = (id) => API.delete(`${targetsAPI}${id}/theme/draft`).then((res) => res.data);

/**
 *
 * @param {number} id The id of the Target being fetched.
 * @returns {Promise<Target>}
 */
const getTarget = (id) => API.get(`${targetsAPI}${id}`).then((res) => res.data);

/**
 * @param {int} id  The id of the Target whose customization is being fetched.
 * @returns {Promise<{}>}
 */
const getTargetCustomization = (id) => API.get(`${targetsAPI}${id}/customization`).then((res) => res.data);

/**
 *
 * @param {int} id  The id of the Target whose Question Sources are being fetched.
 * @returns {Promise<{}>}
 */
const getTargetQuestionSources = (id) => API.get(`${targetsAPI}${id}/question-sources`).then((res) => res.data);

/**
 *
 * @param {int} id  The id of the Target whose Secondary Feeds are being fetched.
 * @returns {Promise<{}>}
 */
const getTargetSecondaryFeeds = (id) => API.get(`${targetsAPI}${id}/feeds`).then((res) => res.data);

/**
 *
 * @param id {int}  The id of the Target whose theme is being fetched.
 * @param whichTheme {string} - Identifies which theme the api should request.
 * @returns {Promise<{}>}
 */
const getTargetTheme = (id, whichTheme = TargetThemes.Published) =>
  API.get(`${targetsAPI}${id}/theme/${whichTheme}`).then((res) => res.data);

/**
 * @typedef TargetSearchResult
 * @property {number} targetId
 * @property {string} uuid
 * @property {string} name
 * @property {Date|string} updatedTime
 */

/**
 * @typedef GetTargetListResponse
 * @property {number} page
 * @property {number} pageTotal
 * @property {number} size
 * @property {number} total
 * @property {TargetSearchResult[]} results
 */

/**
 * @param {object} params
 * @param {string} [params.query]
 * @param {string|null} [params.orderBy]
 * @param {string|null} [params.order]
 * @param {number|string} [params.page]
 * @param {number|string} [params.size]
 * @returns {Promise<GetTargetListResponse>}
 */
const getTargetList = ({ query = "", orderBy, order, page = 1, size = 10 } = {}) =>
  API.get(`${targetsAPI}`, { params: { query, orderBy, order, page, size } }).then((res) => res.data);

/**
 * This endpoint fetches all targets available to the given user.
 * @returns {Promise<import('types').TargetSimple[]>}
 */
const getAllTargets = () => {
  return API.get(`${targetsAPI}all`).then((res) => res.data);
};

/**
 * @typedef {import('types').TargetStatistics} TargetStatistics
 */

/**
 * Fetches statistics for a given target.
 * @param {number | string}  id
 * @returns {Promise<TargetStatistics>}
 */
const getTargetStatistics = (id) => {
  return API.get(`${targetsAPI}${id}/statistics`).then((res) => res.data);
};

/**
 * @param id {int|null} The ID of the target to be updated.
 * @param {Object} request - The target values being updated.
 * @param {string} request.name - The new name of the target.
 * @param {string} request.locale - The new locale of the target.
 * @param {string} request.questionRoleTemplate - The new question role template of the target.
 * @returns {Promise<Target>}
 */
const updateTargetBasicInfo = (id, request) => API.put(`${targetsAPI}${id}/info`, request).then((res) => res.data);

/**
 * @param {int} id  The id of the Target whose customization is being updated.
 * @param {Object} customization - The form data of Target Customization.
 * @returns {Promise<{}>}
 */
const updateTargetCustomization = (id, customization) =>
  API.put(`${targetsAPI}${id}/customization`, customization).then((res) => res.data);

/**
 * @param id {int|null} The ID of the target to be updated.
 * @param {[string]} questionSources - The list of networks to be trusted by target.

 * @returns {Promise<{}>}
 */
const updateTargetQuestionSources = (id, questionSources) =>
  API.put(`${targetsAPI}${id}/question-sources`, questionSources).then((res) => res.data);

/**
 * @param id {int} The ID of the target to be updated.
 * @param {Object} request - The target values being updated.
 * @param {[int]} request.questionFeeds - The List of Feed ids to subscribe the Target to.
 * @returns {Promise<{}>}
 */
const updateTargetSecondaryFeeds = (id, request) =>
  API.put(`${targetsAPI}${id}/feeds`, request).then((res) => res.data);

/**
 * @param targetId {int|null} The ID of the target to be updated.
 * @param {Object} request - The target values being updated.
 * @param {string} request.selectedTheme - Which theme to apply changes.
 * @param {string} request.fontSize - Base font size for widget.
 * @param {string} request.fontFamily - Font Families used for widget.
 * @param {string} request.title - Title of widget.
 * @param {string} request.titleProps - Styling override for widget title.
 * @param {string} request.outerProps - Styling override for widget outer .csw container.
 * @param {string} request.accentColor - Hex color for widget accents.
 * @param {string} request.submitButtonColor - Hex color for widget submit/primary buttons.
 * @param {boolean} request.widgetDarkMode - Identifies if widget supports dark mode.
 * @param {boolean} request.publishDraft - Identifies if api should save draft as published theme
 * @param {string} whichTheme - Identifies which theme the api should request
 * @returns {Promise<{}>}
 */
const updateTargetThemeSettings = (targetId, request, whichTheme = TargetThemes.Published) =>
  API.put(`${targetsAPI}${targetId}/theme/${whichTheme}`, request).then((res) => res.data);

export default {
  create: createTarget,
  clone: cloneTarget,
  delete: deleteTarget,
  discardDraftTheme: discardTargetDraftTheme,
  get: getTarget,
  getAll: getAllTargets,
  getCustomization: getTargetCustomization,
  getQuestionSources: getTargetQuestionSources,
  getSecondaryFeeds: getTargetSecondaryFeeds,
  getTheme: getTargetTheme,
  getStatistics: getTargetStatistics,
  list: getTargetList,
  updateBasicInfo: updateTargetBasicInfo,
  updateCustomization: updateTargetCustomization,
  updateQuestionSources: updateTargetQuestionSources,
  updateSecondaryFeeds: updateTargetSecondaryFeeds,
  updateTheme: updateTargetThemeSettings,
};
